import React, { useRef, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { INCIDENT_CONFIG } from './helper';

/**
 * CanvasOverlay component that adds a custom canvas layer on top of the Leaflet map
 * for drawing heatmaps, custom markers, or other visualizations
 */
function CanvasOverlay({ data = [], drawMethod = 'heatmap' }) {
  const map = useMap();
  const canvasRef = useRef(null);
  
  // Create a custom Leaflet layer that uses Canvas for rendering
  useEffect(() => {
    if (!map) return;
    // Create a custom canvas layer
    const canvasLayer = L.Layer.extend({
      onAdd: function(map) {
        const canvas = L.DomUtil.create('canvas', 'leaflet-layer');
        canvasRef.current = canvas;
        
        // Position the canvas just behind the pane that contains markers
        canvas.style.position = 'absolute';
        canvas.style.top = 0;
        canvas.style.left = 0;
        canvas.style.pointerEvents = 'none';
        canvas.width = map.getSize().x;
        canvas.height = map.getSize().y;
        
        map._panes.overlayPane.appendChild(canvas);
        
        // Set initial size and position
        this._update();
        
        // Add event listeners for map movement/zoom
        map.on('moveend', this._update, this);
        map.on('resize', this._resize, this);
        
        return this;
      },
      
      onRemove: function(map) {
        if (canvasRef.current) {
          map._panes.overlayPane.removeChild(canvasRef.current);
        }
        map.off('moveend', this._update, this);
        map.off('resize', this._resize, this);
      },
      
      _resize: function(e) {
        if (canvasRef.current) {
          canvasRef.current.width = map.getSize().x;
          canvasRef.current.height = map.getSize().y;
          this._update();
        }
      },
      
      _update: function() {
        if (!canvasRef.current) return;
        
        const topLeft = map.containerPointToLayerPoint([0, 0]);
        L.DomUtil.setPosition(canvasRef.current, topLeft);
        
        // Render the data on the canvas
        this._render();
      },
      
      _render: function() {
        if (!canvasRef.current) return;
        
        const ctx = canvasRef.current.getContext('2d');
        const size = map.getSize();
        
        // Clear the canvas
        ctx.clearRect(0, 0, size.x, size.y);
        
        // Choose rendering method based on prop
        switch(drawMethod) {
          case 'heatmap':
            this._drawHeatmap(ctx, data);
            break;
          case 'clusters':
            this._drawClusters(ctx, data);
            break;
          case 'density':
            this._drawDensity(ctx, data);
            break;
          default:
            this._drawHeatmap(ctx, data);
        }
      },

     _drawHeatmap: function(ctx, incidents) {
        incidents.forEach(incident => {
          const { lat, long } = incident?.incidentLocation || {};
          if (!lat || !long) return;
      
          const point = map.latLngToContainerPoint([lat, long]);
          const radius = 8;
      
          // Get color based on incident type using INCIDENT_CONFIG
          const incidentTypeId = incident.incidentTypeId || '';
const incidentTypes = localStorage.getItem('incidentTypes') ? JSON.parse(localStorage.getItem('incidentTypes')) : [];
          const type = incidentTypes?.find(inc=>inc.incidentTypeId === incidentTypeId)?.incidentName || '';
          const configKey = Object.keys(INCIDENT_CONFIG).find((key) =>
            type.toLowerCase().includes(key.toLowerCase())
          );
          const color = INCIDENT_CONFIG[configKey]?.bgColor || INCIDENT_CONFIG.Other.bgColor;
      
          const gradient = ctx.createRadialGradient(
            point.x, point.y, 0,
            point.x, point.y, radius
          );
      
          gradient.addColorStop(0, color);
          gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
          ctx.fill();
        });
      },
      
      
      _drawClusters: function(ctx, incidents) {
        // Group incidents by location proximity
        const cellSize = 50; // pixels
        const clusters = {};
        
        incidents.forEach(incident => {
          if (!incident?.incidentLocation?.lat || !incident?.incidentLocation?.long) return;
          
          // Convert lat/lng to pixel coordinates
          const point = map.latLngToContainerPoint([
            incident.incidentLocation.lat,
            incident.incidentLocation.long
          ]);
          
          // Group by cell
          const cellX = Math.floor(point.x / cellSize);
          const cellY = Math.floor(point.y / cellSize);
          const cellId = `${cellX},${cellY}`;
          
          if (!clusters[cellId]) {
            clusters[cellId] = {
              x: cellX * cellSize + cellSize / 2,
              y: cellY * cellSize + cellSize / 2,
              count: 0
            };
          }
          
          clusters[cellId].count++;
        });
        
        // Draw clusters
        Object.values(clusters).forEach(cluster => {
          const radius = Math.min(Math.max(10, cluster.count * 3), 30);
          
          ctx.beginPath();
          ctx.arc(cluster.x, cluster.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 0, 0, ${Math.min(0.2 + (cluster.count / 20), 0.8)})`;
          ctx.fill();
          
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#fff';
          ctx.font = '12px sans-serif';
          ctx.fillText(cluster.count.toString(), cluster.x, cluster.y);
        });
      },
      
      _drawDensity: function(ctx, incidents) {
        // Create density map
        const size = map.getSize();
        const width = size.x;
        const height = size.y;
        const density = new Array(width * height).fill(0);
        
        incidents.forEach(incident => {
          if (!incident?.incidentLocation?.lat || !incident?.incidentLocation?.long) return;
          
          // Convert lat/lng to pixel coordinates
          const point = map.latLngToContainerPoint([
            incident.incidentLocation.lat,
            incident.incidentLocation.long
          ]);
          
          // Add to density map with a decay function
          const x = Math.floor(point.x);
          const y = Math.floor(point.y);
          
          const radius = 30;
          
          // Add density around the point with decay
          for (let dx = -radius; dx <= radius; dx++) {
            for (let dy = -radius; dy <= radius; dy++) {
              const nx = x + dx;
              const ny = y + dy;
              
              if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
              
              const distance = Math.sqrt(dx * dx + dy * dy);
              if (distance > radius) continue;
              
              const decay = 1 - (distance / radius);
              density[ny * width + nx] += decay;
            }
          }
        });
        
        // Find max density
        const maxDensity = Math.max(...density);
        
        // Draw density map
        const imageData = ctx.createImageData(width, height);
        
        for (let i = 0; i < density.length; i++) {
          const value = density[i] / maxDensity;
          
          if (value > 0.05) {
            // Create color gradient from blue to red based on density
            const r = Math.min(255, Math.floor(value * 2 * 255));
            const g = Math.min(255, Math.floor((1 - value) * 255));
            const b = Math.floor((1 - value * 2) * 255);
            const a = Math.min(255, Math.floor(value * 200) + 50);
            
            imageData.data[i * 4] = r;
            imageData.data[i * 4 + 1] = g;
            imageData.data[i * 4 + 2] = b;
            imageData.data[i * 4 + 3] = a;
          }
        }
        
        ctx.putImageData(imageData, 0, 0);
      }
    });
    
    // Add the layer to the map
    const overlay = new canvasLayer();
    overlay.addTo(map);
    
    // Clean up
    return () => {
      map.removeLayer(overlay);
    };
  }, [map, data, drawMethod]);
  
  return null;
}

export default CanvasOverlay;